import React, { useState } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AnalyzeForm } from './components/AnalyzeForm';
import { DetectionResults } from './components/DetectionResults';
import { GeneratedSchema } from './components/GeneratedSchema';
import { RateLimitBanner } from './components/RateLimitBanner';
import {
  scrapeUrl,
  detectTypes,
  generateForType,
  RateLimitError,
} from './lib/api';
import type { AppState, GenerationResult } from './types';
import { hasExistingOfType } from './lib/existing';

const initialState: AppState = {
  status: 'idle',
  html: null,
  sourceUrl: null,
  sourceTitle: null,
  facts: null,
  detection: null,
  score: null,
  generations: [],
  generatingType: null,
  bulkProgress: null,
  error: null,
  rateLimit: null,
};

export default function App() {
  const [state, setState] = useState<AppState>(initialState);

  const handleErr = (err: unknown, prevPartial?: Partial<AppState>) => {
    if (err instanceof RateLimitError) {
      setState((s) => ({ ...s, ...prevPartial, status: 'rate-limited', rateLimit: err.info, generatingType: null, bulkProgress: null }));
    } else {
      const message = err instanceof Error ? err.message : 'Request failed';
      setState((s) => ({ ...s, ...prevPartial, status: 'error', error: message, generatingType: null, bulkProgress: null }));
    }
  };

  const runDetection = async (html: string, sourceUrl?: string, sourceTitle?: string) => {
    setState({
      ...initialState,
      status: 'detecting',
      html,
      sourceUrl: sourceUrl || null,
      sourceTitle: sourceTitle || null,
    });
    try {
      const { facts, detection, score } = await detectTypes(html, sourceUrl);
      setState((s) => ({
        ...s,
        status: 'detected',
        facts,
        detection,
        score: score || null,
        sourceUrl: sourceUrl || facts.url || null,
        error: null,
      }));
    } catch (err) {
      handleErr(err, { html, sourceUrl: sourceUrl || null });
    }
  };

  const handleHtml = async (html: string) => {
    await runDetection(html);
  };

  const handleUrl = async (url: string) => {
    setState({ ...initialState, status: 'scraping' });
    try {
      const scrape = await scrapeUrl(url);
      setState((s) => ({
        ...s,
        status: 'detecting',
        html: scrape.html,
        sourceUrl: scrape.sourceUrl,
        sourceTitle: scrape.sourceTitle || null,
        facts: scrape.facts,
      }));
      const { detection, score } = await detectTypes(scrape.html, scrape.sourceUrl);
      setState((s) => ({
        ...s,
        status: 'detected',
        detection,
        score: score || null,
        error: null,
      }));
    } catch (err) {
      handleErr(err);
    }
  };

  /**
   * Generate a JSON-LD object for the chosen type.
   *
   * Behavior:
   * - If this type isn't already in `generations`, append (becomes companion if
   *   primary already exists; primary if list is empty).
   * - If this type IS already in the list, replace the existing one (used for
   *   re-augmenting or re-generating fresh).
   *
   * Replaces never re-scrape; the cached HTML/facts are reused.
   */
  const handleGenerate = async (typeName: string, augment = false) => {
    if (!state.html) return;
    setState((s) => ({ ...s, status: 'generating', generatingType: typeName, error: null }));
    try {
      const generation = await generateForType(
        state.html,
        typeName,
        state.sourceUrl || undefined,
        augment
      );
      setState((s) => {
        const existingIdx = s.generations.findIndex((g) => g.type === typeName);
        let nextGens: GenerationResult[];
        if (existingIdx >= 0) {
          nextGens = [...s.generations];
          nextGens[existingIdx] = generation;
        } else {
          nextGens = [...s.generations, generation];
        }
        return { ...s, status: 'generated', generations: nextGens, generatingType: null };
      });
    } catch (err) {
      handleErr(err, { status: state.generations.length > 0 ? 'generated' : 'detected' });
    }
  };

  /**
   * Generate every detected candidate in one shot. Detection already caps
   * the list at <=5 quality-filtered types, so we don't apply a second
   * threshold here — that would mismatch what the user sees on the cards.
   *
   * Existing generations of the same type get replaced; new types are
   * appended into the @graph. Sequential calls so burst limits stay happy
   * and per-type progress is clear.
   */
  const handleGenerateAll = async () => {
    if (!state.html || !state.detection || !state.facts) return;

    const html = state.html;
    const sourceUrl = state.sourceUrl || undefined;
    const facts = state.facts;
    const candidates = state.detection.candidates.map((c) => c.type);

    if (candidates.length === 0) return;

    setState((s) => ({
      ...s,
      status: 'generating',
      bulkProgress: { current: 0, total: candidates.length },
      error: null,
    }));

    for (let i = 0; i < candidates.length; i++) {
      const typeName = candidates[i];
      const augment = hasExistingOfType(facts.existingJsonLd, typeName);
      setState((s) => ({
        ...s,
        generatingType: typeName,
        bulkProgress: { current: i + 1, total: candidates.length },
      }));
      try {
        const generation = await generateForType(html, typeName, sourceUrl, augment);
        setState((s) => {
          const existingIdx = s.generations.findIndex((g) => g.type === typeName);
          let nextGens: GenerationResult[];
          if (existingIdx >= 0) {
            nextGens = [...s.generations];
            nextGens[existingIdx] = generation;
          } else {
            nextGens = [...s.generations, generation];
          }
          return { ...s, generations: nextGens };
        });
      } catch (err) {
        handleErr(err);
        return;
      }
    }

    setState((s) => ({
      ...s,
      status: 'generated',
      generatingType: null,
      bulkProgress: null,
    }));
  };

  // The "Pick a different type" button — go back to detection but KEEP what's
  // already generated. From there the user can re-pick a candidate (which
  // replaces if the type matches, or appends to the @graph if new).
  const handleBackToAnalysis = () => {
    setState((s) => ({ ...s, status: 'detected', error: null }));
  };

  const handleViewGenerated = () => {
    setState((s) => ({ ...s, status: 'generated', error: null }));
  };

  const handleRemoveGeneration = (index: number) => {
    setState((s) => {
      const next = s.generations.filter((_, i) => i !== index);
      if (next.length === 0) {
        return { ...s, status: 'detected', generations: [], error: null };
      }
      return { ...s, generations: next };
    });
  };

  const handleStartOver = () => {
    setState(initialState);
  };

  const busyForm =
    state.status === 'scraping' ||
    state.status === 'detecting';

  const busyFormLabel =
    state.status === 'scraping' ? 'Fetching page…'
      : state.status === 'detecting' ? 'Detecting types…'
        : undefined;

  // True only while a generate request is in flight
  const isGenerating = state.status === 'generating';

  return (
    <>
      <Header />

      <main>
        <nav aria-label="Breadcrumb" className="breadcrumbs">
          <div className="container-narrow">
            <ol>
              <li><a href="https://theseoconsultant.ai/">Home</a></li>
              <li><a href="https://theseoconsultant.ai/ai-seo-tools/">AI SEO Tools</a></li>
              <li aria-current="page">Schema Markup Generator</li>
            </ol>
          </div>
        </nav>
        <section className="section">
          <div className="container-narrow">
            <p className="eyebrow" style={{ marginBottom: 16 }}>Structured data · Schema.org</p>
            <h1 className="hero-headline">
              Generate <em>schema markup</em> any URL deserves.
            </h1>
            <p className="lead" style={{ marginBottom: 40 }}>
              Drop in a URL or paste rendered HTML. We detect which Schema.org types fit
              the page, generate valid JSON-LD grounded in your real content, and export
              it as JSON-LD, Microdata, or RDFa.
            </p>

            {state.status !== 'detected' && state.status !== 'generated' && (
              <>
                <AnalyzeForm
                  busy={busyForm}
                  busyLabel={busyFormLabel}
                  onSubmitHtml={handleHtml}
                  onSubmitUrl={handleUrl}
                />
                {state.rateLimit && <RateLimitBanner info={state.rateLimit} />}
                {state.error && !state.rateLimit && (
                  <div
                    role="alert"
                    style={{
                      marginTop: 16,
                      padding: '12px 16px',
                      background: 'var(--score-low-wash)',
                      border: '1px solid rgba(179,58,58,0.25)',
                      color: 'var(--score-low)',
                      borderRadius: 4,
                      fontSize: 14,
                    }}
                  >
                    {state.error}
                  </div>
                )}
              </>
            )}

            {(state.status === 'detected' || state.status === 'generating') &&
              state.detection && state.facts && (
              <div className="animate-in">
                <SourceHeader
                  url={state.sourceUrl}
                  title={state.sourceTitle}
                  onStartOver={handleStartOver}
                  generationsCount={state.generations.length}
                  onViewGenerated={handleViewGenerated}
                />
                <DetectionResults
                  detection={state.detection}
                  facts={state.facts}
                  score={state.score}
                  busy={isGenerating}
                  generatingType={state.generatingType}
                  bulkProgress={state.bulkProgress}
                  generatedTypes={state.generations.map((g) => g.type)}
                  onGenerate={handleGenerate}
                  onGenerateAll={handleGenerateAll}
                />
                {state.error && (
                  <div
                    role="alert"
                    style={{
                      marginTop: 16,
                      padding: '12px 16px',
                      background: 'var(--score-low-wash)',
                      border: '1px solid rgba(179,58,58,0.25)',
                      color: 'var(--score-low)',
                      borderRadius: 4,
                      fontSize: 14,
                    }}
                  >
                    {state.error}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {(state.status === 'generated' || (state.status === 'generating' && state.generations.length > 0)) &&
          state.generations.length > 0 && state.facts && (
          <section className="section section-paper-2">
            <div className="container-narrow animate-in">
              <SourceHeader
                url={state.sourceUrl}
                title={state.sourceTitle}
                onStartOver={handleStartOver}
                onBackToAnalysis={handleBackToAnalysis}
              />
              <GeneratedSchema
                generations={state.generations}
                facts={state.facts}
                busy={isGenerating}
                generatingType={state.generatingType}
                onChooseDifferentType={handleBackToAnalysis}
                onAddCompanion={handleGenerate}
                onRemove={handleRemoveGeneration}
              />
              <div className="print-byline" aria-hidden>
                Created by Richard Wong / The SEO Consultant.ai LLC ·{' '}
                <a href="https://theseoconsultant.ai">theseoconsultant.ai</a>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}

const SourceHeader: React.FC<{
  url: string | null;
  title: string | null;
  onStartOver: () => void;
  onBackToAnalysis?: () => void;
  onViewGenerated?: () => void;
  generationsCount?: number;
}> = ({ url, title, onStartOver, onBackToAnalysis, onViewGenerated, generationsCount }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 16,
      marginBottom: 24,
      flexWrap: 'wrap',
    }}
    className="no-print"
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      {url ? (
        <>
          <span className="eyebrow">Analyzing</span>
          <p style={{ margin: '6px 0 0', fontSize: 15, color: 'var(--ink-2)', wordBreak: 'break-word' }}>
            {title && <strong style={{ fontWeight: 600 }}>{title} · </strong>}
            <a href={url} target="_blank" rel="noopener">
              {url}
            </a>
          </p>
        </>
      ) : (
        <>
          <span className="eyebrow">Analyzing</span>
          <p style={{ margin: '6px 0 0', fontSize: 15, color: 'var(--ink-3)' }}>
            Pasted HTML
          </p>
        </>
      )}
    </div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {onBackToAnalysis && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={onBackToAnalysis}>
          ← Back to analysis
        </button>
      )}
      {onViewGenerated && (generationsCount ?? 0) > 0 && (
        <button type="button" className="btn btn-signal btn-sm" onClick={onViewGenerated}>
          View generated ({generationsCount}) →
        </button>
      )}
      <button type="button" className="btn btn-ghost btn-sm" onClick={onStartOver}>
        Start over
      </button>
    </div>
  </div>
);
